import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { 
  Check, 
  ArrowRight, 
  Zap, 
  TrendingUp, 
  ShoppingCart, 
  Clock, 
  Users, 
  Palette, 
  Code,
  Smartphone,
  Search,
  Shield,
  Headphones,
  Star,
  Crown,
  Rocket
} from 'lucide-react';

const WebDevelopment = () => {
  const foundationFeatures = [
    "3-5 Professional Pages",
    "Mobile-Responsive Design", 
    "Basic SEO Setup",
    "Contact Form Integration",
    "Social Media Links",
    "User-Friendly CMS (WordPress)",
    "SSL Certificate Included",
    "Basic Training Session",
    "1 Month Free Support",
    "Content Update Training"
  ];

  const growthFeatures = [
    "Everything in Foundation",
    "8-15 Custom Pages",
    "Custom UI/UX Design",
    "Advanced SEO Optimization",
    "Booking System Integration",
    "Review Feed Integration",
    "CRM Synchronization",
    "Performance Optimization",
    "1-Hour Training Session",
    "Client Resource Hub Access",
    "Video Tutorial Library",
    "3 Months Free Support"
  ];

  const storefrontFeatures = [
    "Everything in Growth Engine",
    "Full Product Catalog",
    "Inventory Management System",
    "Secure Payment Gateways",
    "Order Management",
    "Customer Account Portal",
    "Shipping Integration",
    "Tax Calculation",
    "Analytics & Reporting",
    "Advanced Security Features",
    "Comprehensive Training Program",
    "6 Months Priority Support"
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-primary/10 text-primary border-primary/20 font-body mb-4">
            Website Development Packages
          </Badge>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-6">
            Professional Websites
            <span className="text-gradient"> Built for Your Success</span>
          </h1>
          <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            From rapid-launch solutions for startups to comprehensive e-commerce platforms, 
            we have the perfect package to fit your business needs and budget.
          </p>
        </div>
      </section>

      {/* Special Offer Banner */}
      <section className="py-4 bg-accent text-accent-foreground">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Star className="h-5 w-5" />
            <span className="font-heading font-bold">20th Anniversary Special:</span>
            <span className="font-body">Starter Web Pack for $999 - Limited to first 10 businesses!</span>
            <Button asChild variant="secondary" size="sm" className="ml-4">
              <Link to="/booking">Claim Offer</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            
            {/* Foundation Package */}
            <Card className="relative hover-lift shadow-card border-2 border-border">
              <CardHeader className="text-center pb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="font-heading text-2xl font-bold text-foreground">Foundation</CardTitle>
                <p className="font-body text-muted-foreground">Perfect for startups and micro-businesses</p>
                <div className="mt-4">
                  <span className="font-heading text-4xl font-bold text-primary">$1,500</span>
                  <span className="font-body text-muted-foreground ml-2">- $3,000</span>
                </div>
                <p className="font-body text-xs text-muted-foreground mt-2">
                  Rapid launch for new businesses
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {foundationFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Check className="h-4 w-4 text-accent flex-shrink-0" />
                      <span className="font-body text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="pt-4">
                  <Button 
                    asChild 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold"
                  >
                    <Link to="/booking">
                      Start Foundation Project
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>

                <div className="text-center pt-2">
                  <p className="font-body text-xs text-muted-foreground">
                    Best for: New businesses, consultants, service providers
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Growth Engine Package */}
            <Card className="relative hover-lift shadow-elegant border-2 border-accent bg-gradient-to-br from-white to-accent/5">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-accent text-accent-foreground font-body font-semibold px-4 py-1">
                  Most Popular
                </Badge>
              </div>
              <CardHeader className="text-center pb-8">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-accent" />
                </div>
                <CardTitle className="font-heading text-2xl font-bold text-foreground">Growth Engine</CardTitle>
                <p className="font-body text-muted-foreground">For established businesses needing an upgrade</p>
                <div className="mt-4">
                  <span className="font-heading text-4xl font-bold text-accent">$5,000</span>
                  <span className="font-body text-muted-foreground ml-2">- $12,000</span>
                </div>
                <p className="font-body text-xs text-muted-foreground mt-2">
                  Professional upgrade with advanced features
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {growthFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Check className="h-4 w-4 text-accent flex-shrink-0" />
                      <span className="font-body text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="pt-4">
                  <Button 
                    asChild 
                    className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-heading font-semibold"
                  >
                    <Link to="/booking">
                      Start Growth Project
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>

                <div className="text-center pt-2">
                  <p className="font-body text-xs text-muted-foreground">
                    Best for: Growing businesses, professional services, agencies
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Storefront Package */}
            <Card className="relative hover-lift shadow-card border-2 border-primary/30">
              <CardHeader className="text-center pb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingCart className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="font-heading text-2xl font-bold text-foreground">Storefront</CardTitle>
                <p className="font-body text-muted-foreground">Complete e-commerce solution</p>
                <div className="mt-4">
                  <span className="font-heading text-4xl font-bold text-primary">$7,500</span>
                  <span className="font-body text-muted-foreground ml-2">- $20,000+</span>
                </div>
                <p className="font-body text-xs text-muted-foreground mt-2">
                  Full-featured online store
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {storefrontFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Check className="h-4 w-4 text-accent flex-shrink-0" />
                      <span className="font-body text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="pt-4">
                  <Button 
                    asChild 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold"
                  >
                    <Link to="/booking">
                      Start E-commerce Project
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>

                <div className="text-center pt-2">
                  <p className="font-body text-xs text-muted-foreground">
                    Best for: Product sellers, online retailers, subscription services
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fixed-Fee Promise */}
          <div className="mt-16 text-center">
            <Card className="max-w-2xl mx-auto shadow-elegant bg-gradient-to-r from-accent/10 to-primary/10">
              <CardContent className="p-8">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                  Fixed-Fee Promise
                </h3>
                <p className="font-body text-muted-foreground">
                  No hourly creep, no surprise costs. Every package is a fixed fee so you have 
                  complete financial control from day one. What we quote is what you pay.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Our Proven Development Process</h2>
            <p className="font-body text-lg text-primary-foreground/80 max-w-2xl mx-auto">
              20 years of experience has taught us exactly how to deliver exceptional results, on time and on budget.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">1. Discovery</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                We learn about your business, goals, and target audience to create the perfect strategy.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Palette className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">2. Design</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                We create a beautiful, user-friendly design that represents your brand perfectly.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Code className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">3. Development</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                We build your website with clean code, best practices, and mobile optimization.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Rocket className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">4. Launch & Train</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                We launch your site and provide comprehensive training for complete control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Our Development */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Why Choose Our Development Services?
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              We don't just build websites - we create digital assets that grow your business 
              while keeping you in complete control.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="hover-lift shadow-card text-center">
              <CardContent className="p-6">
                <Clock className="h-8 w-8 text-accent mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-foreground mb-2">Rapid Delivery</h3>
                <p className="font-body text-sm text-muted-foreground">
                  Foundation sites launch in 2-3 weeks. Growth Engine projects in 4-6 weeks. 
                  No endless delays.
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift shadow-card text-center">
              <CardContent className="p-6">
                <Shield className="h-8 w-8 text-accent mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-foreground mb-2">Complete Control</h3>
                <p className="font-body text-sm text-muted-foreground">
                  Every site includes training and documentation. You'll never be held hostage 
                  by your developer again.
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift shadow-card text-center">
              <CardContent className="p-6">
                <TrendingUp className="h-8 w-8 text-accent mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-foreground mb-2">Built to Convert</h3>
                <p className="font-body text-sm text-muted-foreground">
                  Every element is designed to turn visitors into customers. 
                  Your website becomes a sales machine.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-accent/10 to-primary/10">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              Ready to Launch Your Website?
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Let's discuss your project and find the perfect development package for your business needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/booking">
                  Schedule Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg" 
                className="font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/social-media">View Social Media Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebDevelopment;